import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { LBtn } from "../../../../../common/components/LBtn";
import { LKebabDropdown } from "../../../../../common/components/LKebabDropdown";
import { LModal } from "../../../../../common/components/LModal";
import { LTooltip } from "../../../../../common/components/LTooltip";
import { ExistingUser, InviteUserArray, RoleId } from "@store/generalTypes";
import { useApi } from "../../../../../services/useApi";
import { useQuery } from "../../../../../utils/useQuery";
import { AssignStaffHeader } from "../components/AccountLocations/AssignStaff/AssignStaffHeader";
import { AssignStaffUserModal } from "../components/AccountLocations/AssignStaff/AssignStaffUserModal";
import { ClinicianConfirmation } from "../components/AccountLocations/ClinicianConfirmation";
import { StaffModalFooter } from "../components/StaffModalFooter";
import { AssignClinicianStaffBody } from "../components/AccountLocations/AssignStaff/AssignClinicianStaffBody";
import { getUsersByRole } from "@helpers/usersHelper";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { isEmpty } from "lodash";
import { setMyPermission } from "@store/userDetailSlice";
import { Colors } from "@styles/colors";

const RoleRow = ({
    name,
    description,
    assignedTo,
    showTag,
    disabled,
    secondary,
    roleId,
    loading,
    onAssignUser,
    onDeleteUser
}: RoleRowProps): JSX.Element => {
    const { isClinicalAdmin } = useUserPermission({});

    const assignedToFiltered = assignedTo.filter((x) =>
        x.locations?.reduce((prev, curr) => {
            return prev || !!curr?.roles?.map((role) => role.id).includes(roleId);
        }, false)
    );

    const handleOnAssignuser = (): void => {
        onAssignUser(roleId, name);
    };

    const handleOnRemoveUserRole = (userId: string): void => {
        onDeleteUser(roleId, name, userId);
    };

    const renderAddUser = (): JSX.Element => {
        if (!isClinicalAdmin) {
            return <></>;
        }

        return assignedToFiltered.length ? (
            <a className="mt-4 has-text-weight-bold" onClick={handleOnAssignuser}>
                + Add another user
            </a>
        ) : (
            <LBtn
                onClick={handleOnAssignuser}
                smallSpacing
                variation={secondary ? "outline" : ""}
                disabled={disabled}
                isBold
            >
                <p className="mx-4">Assign user</p>
            </LBtn>
        );
    };

    return (
        <>
            <div className="column is-2 has-text-weight-bold">
                <span className="ml-2">{name}</span>
            </div>
            <div className="column">
                <div className="is-flex is-flex-direction-column is-align-items-baseline">
                    {description}
                    {showTag && (
                        <div className="is-flex has-background-light is-bordered-top is-bordered-bottom">
                            <span className="tag is-info is-light">
                                Can see <p className="has-text-weight-bold ml-1"> sensitive examinee info</p>
                            </span>
                            <LTooltip text="The clinician role has access to sensitive clinical information which includes:\n Examinee’s full name, birth date, gender, level of education, assessment results, etc.\nPlease make sure this assignment is in line with your organization’s rules, regulations and procedures" />
                        </div>
                    )}
                </div>
            </div>
            <div className="column">
                {assignedToFiltered.map((x) => {
                    return (
                        <div key={x.email} className="is-flex is-justify-content-space-between">
                            <div className="is-flex">
                                <p className="has-text-weight-bold mr-2 no-shrink">{`${x.firstName} ${x.lastName}`}</p>
                                <p className="has-text-overflow-ellipsis px-2" title={x.email}>{`<${x.email}>`}</p>
                                {!x.locations?.some((l) => l?.acceptedRoles?.map((r) => r?.id).includes(roleId)) && (
                                    <LTooltip
                                        text="User did not accept invite yet."
                                        icon="warning"
                                        color={Colors.errorRedBg}
                                    />
                                )}
                            </div>
                            {isClinicalAdmin && (
                                <LKebabDropdown
                                    elements={
                                        <a
                                            className="has-text-primary-blue"
                                            onClick={() => handleOnRemoveUserRole(x.id)}
                                        >
                                            Remove
                                        </a>
                                    }
                                />
                            )}
                        </div>
                    );
                })}
                {loading ? (
                    <div style={{ width: 200 }}>
                        <Skeleton />
                        <Skeleton />
                    </div>
                ) : (
                    renderAddUser()
                )}
            </div>
        </>
    );
};

interface RoleRowProps {
    name: string;
    description: string;
    assignedTo: ExistingUser[];
    roleId: RoleId;
    showTag?: boolean;
    disabled?: boolean;
    secondary?: boolean;
    loading: boolean;
    onAssignUser: (role: RoleId, roleName: string) => void;
    onDeleteUser: (role: RoleId, roleName: string, userId: string) => void;
}

export const AccountLocationDetails = (): JSX.Element => {
    const { searchParams } = useQuery();

    const locationId = Number(searchParams.get("locationId"));
    const locationName = searchParams.get("locationName");

    const organization = useAppSelector((x) => x.organization);
    const { getOrganizationUsers, loading, deleteUserRoleByLocation, batchAssignLocationUser, getMyPermission } =
        useApi();
    const dispatch = useAppDispatch();

    const toggleStaffModalOn = (role: RoleId, roleName: string): void => {
        setRoleToAdd(role);
        setRoleName(roleName);

        setAssignStaffModalOpen(true);
    };

    const toggleStaffModalOff = (): void => {
        setRoleToAdd(undefined);
        setRoleName(undefined);

        setAssignStaffModalOpen(false);
    };

    const toggleDeleteModalOn = (role: RoleId, roleName: string, userId: string): void => {
        setRoleToAdd(role);
        setRoleName(roleName);
        setUserId(userId);

        setDeleteUserModalOpen(true);
    };

    const toggleDeleteModalOff = (): void => {
        setRoleToAdd(undefined);
        setRoleName(undefined);
        setUserId(undefined);

        setDeleteUserModalOpen(false);
    };

    const promptClinicianModal = (role: RoleId, roleName: string): void => {
        setRoleToAdd(role);
        setRoleName(roleName);

        setClinicianModalOpen(true);
    };

    const closeClinicianModal = (confirm?: boolean): void => {
        setClinicianModalOpen(false);

        if (confirm) {
            setAssignStaffModalOpen(true);
        }
    };

    const toggleAssignToClinicianModal = (users?: InviteUserArray): void => {
        setClinicianStaffUsersToAdd(users);
        setAssignToClinicianModalOpen(!assignToClinicianModalOpen);

        if (!users) {
            void handleGetOrganizationUsers();
        }
    };

    const handleUpdateClinicianStaff = (users: InviteUserArray): void => {
        setClinicianStaffUsersToAdd(users);
    };

    const [roleToAdd, setRoleToAdd] = useState<RoleId>();
    const [roleName, setRoleName] = useState<string>();
    const [userId, setUserId] = useState<string>();

    const [assignStaffModalOpen, setAssignStaffModalOpen] = useState(false);
    const [clinicianModalOpen, setClinicianModalOpen] = useState(false);
    const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
    const [assignToClinicianModalOpen, setAssignToClinicianModalOpen] = useState(false);
    const [clinicianStaffUsersToAdd, setClinicianStaffUsersToAdd] = useState<InviteUserArray>();

    const [existingUsers, setExistingUsers] = useState<ExistingUser[]>([]);

    const handleGetOrganizationUsers = async (): Promise<void> => {
        const orgUsers = await getOrganizationUsers(organization.id!);

        const locationsFilteredUsers = orgUsers.items.map((u) => {
            if (!isEmpty(u.locations)) {
                const userLocation = u.locations!.findIndex((l) => l.id === Number(locationId));
                u.locations = [u.locations![userLocation]];
            }
            return u;
        });

        setExistingUsers(locationsFilteredUsers);

        const myPermission = await getMyPermission();
        dispatch(setMyPermission(myPermission));
    };

    const handleDeleteUser = async (): Promise<void> => {
        await deleteUserRoleByLocation(userId!, roleToAdd as number, Number(locationId));

        toggleDeleteModalOff();
        void handleGetOrganizationUsers();
    };

    useEffect(() => {
        void handleGetOrganizationUsers();
    }, []);

    return (
        <>
            <div className="has-text-weight-bold is-size-5">{locationName}</div>
            <div className="is-flex">
                <p>Assign staff roles to users for this location.</p>
                <p className="has-text-weight-bold">You may assign multiple roles per user.</p>
            </div>
            <div className="divider" />
            <div className="columns">
                <div className="column is-1">
                    <div className="tag is-success">REQUIRED</div>
                    <div className="has-text-weight-bold mt-2">Main roles</div>
                </div>
                <div className="column mx-6">
                    <div className="columns has-background-light-gray-2 is-bordered-top">
                        <div className="column is-2 has-text-weight-semibold">Role name</div>
                        <div className="column has-text-weight-semibold">Description</div>
                        <div className="column has-text-weight-semibold">Assigned to:</div>
                    </div>
                    <div className="columns py-5 has-background-white is-align-items-center has-text-ldark-gray">
                        <RoleRow
                            name="Admin"
                            description="Main admin of the account (at least 1 user must have this role)"
                            assignedTo={existingUsers}
                            onAssignUser={toggleStaffModalOn}
                            roleId={RoleId.Admin}
                            loading={loading}
                            onDeleteUser={toggleDeleteModalOn}
                        />
                    </div>
                    <div className="columns py-5 has-background-white is-align-items-center has-text-ldark-gray border-top-separator">
                        <RoleRow
                            name="Facilitator"
                            description="Facilitates the LASSI-D™ assessment (at least 1 user must have this role)"
                            assignedTo={existingUsers}
                            onAssignUser={toggleStaffModalOn}
                            roleId={RoleId.Facilitator}
                            loading={loading}
                            onDeleteUser={toggleDeleteModalOn}
                        />
                    </div>
                    <div className="columns py-5 has-background-white is-bordered-bottom table-shadow is-align-items-center border-top-separator has-text-ldark-gray">
                        <RoleRow
                            name="Clinician"
                            description="Evaluating and communicating results to examinees and discussing action plan"
                            assignedTo={existingUsers}
                            showTag
                            onAssignUser={promptClinicianModal}
                            roleId={RoleId.Clinician}
                            loading={loading}
                            onDeleteUser={toggleDeleteModalOn}
                        />
                    </div>
                </div>
            </div>
            <div className="divider" />
            <div className="columns mt-3">
                <div className="column is-1">
                    <div className="tag is-accent-dolphin has-text-white">OPTIONAL</div>
                    <div className="has-text-weight-bold mt-2">Other roles</div>
                </div>
                <div className="column mx-6">
                    <div className="columns has-background-light-gray-2 is-bordered-top">
                        <div className="column is-2 has-text-weight-semibold">Role name</div>
                        <div className="column has-text-weight-semibold">Description</div>
                        <div className="column has-text-weight-semibold">Assigned to:</div>
                    </div>
                    <div className="columns py-5 has-background-white is-align-items-center has-text-ldark-gray">
                        <RoleRow
                            name="Clinical Staff"
                            description="Examples include an RN, LPN, a psychologist, etc. Must work with one or more clinicians."
                            assignedTo={existingUsers}
                            showTag
                            secondary
                            onAssignUser={promptClinicianModal}
                            roleId={RoleId.ClinicalStaff}
                            loading={loading}
                            disabled={
                                !existingUsers.some((x) =>
                                    x.locations?.some((l) => l?.roles?.map((r) => r?.id).includes(RoleId.Clinician))
                                )
                            }
                            onDeleteUser={toggleDeleteModalOn}
                        />
                    </div>
                    <div className="columns py-5 has-background-white is-align-items-center has-text-ldark-gray border-top-separator">
                        <RoleRow
                            name="Researcher"
                            description="Ability to review and analyse LASSI-D™ assessment results and reports"
                            assignedTo={existingUsers}
                            secondary
                            onAssignUser={toggleStaffModalOn}
                            roleId={RoleId.Researcher}
                            loading={loading}
                            onDeleteUser={toggleDeleteModalOn}
                        />
                    </div>
                    <div className="columns py-5 has-background-white is-bordered-bottom table-shadow is-align-items-center border-top-separator has-text-ldark-gray">
                        <RoleRow
                            name="Data Quality Analyst"
                            description="Reviewing LASSI-D™ assessment results to ensure integrity of the data being collected"
                            assignedTo={existingUsers}
                            secondary
                            onAssignUser={toggleStaffModalOn}
                            roleId={RoleId.DataQA}
                            loading={loading}
                            onDeleteUser={toggleDeleteModalOn}
                        />
                    </div>
                </div>
            </div>

            {assignStaffModalOpen ? (
                <LModal
                    title={<AssignStaffHeader name={locationName!} role={roleName} />}
                    onClose={toggleStaffModalOff}
                    width="s"
                >
                    <AssignStaffUserModal
                        refresh={handleGetOrganizationUsers}
                        locationId={Number(locationId)}
                        role={roleToAdd}
                        users={existingUsers}
                        openClinicalStaffModal={toggleAssignToClinicianModal}
                    />
                </LModal>
            ) : null}

            {clinicianModalOpen ? (
                <LModal
                    title={
                        <p className="has-text-weight-bold is-size-3 mx-5" style={{ maxWidth: 440 }}>
                            This role will have access to{" "}
                            <span className="is-italic has-text-accent-dolphin">sensitive</span> clinical information
                        </p>
                    }
                    width="s"
                    fullHeight={false}
                    onClose={closeClinicianModal}
                >
                    <ClinicianConfirmation closeModal={closeClinicianModal} locationName={locationName!} />
                </LModal>
            ) : null}

            {deleteUserModalOpen ? (
                <LModal
                    title={
                        <p className="has-text-weight-bold is-size-3 mx-5 has-text-centered">
                            {`Remove this ${roleName}?`}
                        </p>
                    }
                    fullHeight={false}
                    width={"s"}
                    onClose={toggleDeleteModalOff}
                >
                    <div className="is-flex is-flex-direction-column">
                        <p className="content has-text-centered">
                            {`Are you sure you want to remove this user from their role as a ${roleName}? They will lose
                            access to this role’s permissions.`}
                        </p>
                        <div className="divider" />
                        <div className="pt-2">
                            <div className="columns">
                                <div className="column" />
                                <div className="column is-4">
                                    <LBtn variation="outline" fullWidth onClick={toggleDeleteModalOff}>
                                        Cancel
                                    </LBtn>
                                </div>
                                <div className="column is-1" />
                                <div className="column is-4">
                                    <LBtn fullWidth onClick={handleDeleteUser} loading={loading}>
                                        Continue
                                    </LBtn>
                                </div>
                                <div className="column" />
                            </div>
                        </div>
                    </div>
                </LModal>
            ) : null}

            {assignToClinicianModalOpen ? (
                <LModal
                    title={<AssignStaffHeader name={locationName!} role={"Clinical Staff"} hideSubtitle />}
                    onClose={toggleAssignToClinicianModal}
                    footer={<StaffModalFooter items={clinicianStaffUsersToAdd?.users.length ?? 0} loading={loading} />}
                    fullHeight={false}
                >
                    <AssignClinicianStaffBody
                        clinicians={getUsersByRole(existingUsers, RoleId.Clinician, locationId)}
                        staff={clinicianStaffUsersToAdd}
                        setClinicianStaffUsersToAdd={handleUpdateClinicianStaff}
                        toggleAssignToClinicianModal={toggleAssignToClinicianModal}
                        locationName={locationName!}
                        goBack={() => {
                            setRoleToAdd(RoleId.ClinicalStaff);
                            setRoleName("Clinical Staff");
                            setAssignStaffModalOpen(true);
                        }}
                        locationId={locationId}
                        batchAssignLocationUser={batchAssignLocationUser}
                    />
                </LModal>
            ) : null}
        </>
    );
};
